import { useSite } from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { AxiosError } from "axios";
import { ReactNode, useEffect, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import BulkAutomateScoring from "./org/BulkAutomateScoring";
import BulkEditCosts from "./org/BulkEditCosts";
import BulkEditDealTerms from "./org/BulkEditDealTerms";
import BulkEditExportNetworkTariff from "./org/BulkEditExportNetworkTariff";
import BulkEditFinancialPerformanceInRange from "./org/BulkEditFinancialPerformanceInRange";
import BulkEditGeneration from "./org/BulkEditGeneration";
import BulkEditSiteInformation from "./org/BulkEditSiteInformation";
import BulkEditSupplierTariff from "./org/BulkEditSupplierTariff";

const SitesTableBulkEditMenu = ({
  organisation,
  sites,
  selectedSiteIds,
  bulkEditMode,
  setBulkEditMode,
  onSitesUpdate,
  setTableAlertStyle,
  setTableAlertMessage,
  onEnterBulkReview,
}: {
  organisation?: Organisation;
  sites: Site[];
  selectedSiteIds: Set<string>;
  bulkEditMode?: string;
  setBulkEditMode: (mode: undefined | string) => void;
  onSitesUpdate: (
    updates: { [key: string]: any },
    options?: { actionLabel?: string }
  ) => void;
  setTableAlertStyle: (style: string) => void;
  setTableAlertMessage: (
    message: ReactNode | string | undefined | undefined
  ) => void;
  // When provided, the menu surfaces the "Bulk review" actions. The
  // callback receives the review mode and the snapshot of selected
  // sites at click time, and is expected to swap the page into the
  // bulk-review surface.
  onEnterBulkReview?: (args: { mode: string; sites: Site[] }) => void;
}) => {
  const { fetchSitesReport } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });
  const [isReportDebug, setIsReportDebug] = useState(false);

  const selectedSites = sites.filter((s) => selectedSiteIds.has(s.id));
  const selectedSitesOwnershipTypes = new Set(
    selectedSites.flatMap((s) => s.siteOwnerships.map((so) => so.ownership))
  );
  const selectedSitesInvestmentModels = new Set(
    selectedSites.map((s) => s.investmentModel)
  );

  useEffect(() => {
    if (fetchSitesReport.isError) {
      setTableAlertStyle("danger");

      const err = fetchSitesReport.error as AxiosError<any>;
      const errorData =
        err?.response?.data ??
        (err as any)?.data ??
        err?.message ??
        "Unknown error";

      if (errorData && errorData["siteErrors"]) {
        const siteErrors = errorData["siteErrors"] as unknown as Record<
          string,
          string
        >;
        setTableAlertMessage(
          <>
            <div>
              Failed to generate report for {Object.keys(siteErrors).length} out
              of {selectedSites.length} sites
            </div>
            <ul style={{ margin: 0 }}>
              {Object.entries(siteErrors)
                .slice(0, 10)
                .map(([siteId, errorMessage]) => (
                  <li key={siteId}>
                    <Link to={`/site/${siteId}/edit`}>
                      {sites.find((site) => site.id === siteId)?.name ||
                        `Site ${siteId}`}
                    </Link>{" "}
                    - {errorMessage}
                  </li>
                ))}
              {Object.entries(siteErrors).length > 10 && (
                <li>{`and ${Object.entries(siteErrors).length - 10} more...`}</li>
              )}
            </ul>
          </>
        );
      } else {
        setTableAlertMessage(`Error generating report: ${errorData}}`);
      }
      fetchSitesReport.reset();
    }
  }, [
    fetchSitesReport,
    selectedSites.length,
    setTableAlertMessage,
    setTableAlertStyle,
    sites,
  ]);

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          variant="primary"
          id="dropdown-basic"
          disabled={selectedSiteIds.size === 0}
        >
          Act on selected
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("edit-siteinfo");
            }}
          >
            Edit Site information
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("edit-dealterms");
            }}
          >
            Edit Deal & contract terms
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("edit-tariffs");
            }}
          >
            Edit Export & network tariffs
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("edit-generation");
            }}
          >
            Edit Generation
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("edit-costs");
            }}
          >
            Edit Costs & rates
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("edit-supplier");
            }}
          >
            Edit Supplier tariffs, charges, and savings
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("edit-financial-performance");
            }}
          >
            Edit InRange financial performance
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() => {
              setBulkEditMode("automate-scoring");
            }}
          >
            Automate scoring
          </Dropdown.Item>
          {onEnterBulkReview && (
            <>
              <Dropdown.Divider />
              <Dropdown.Header>Bulk review</Dropdown.Header>
              <Dropdown.Item
                as="button"
                onClick={() =>
                  onEnterBulkReview({
                    mode: "boundary-and-roof",
                    sites: selectedSites,
                  })
                }
              >
                Review building boundary &amp; roof objects
              </Dropdown.Item>
            </>
          )}
          <Dropdown.Divider />
          {(selectedSitesOwnershipTypes.has("landlord") ||
            selectedSitesOwnershipTypes.has("tenant")) &&
            (selectedSitesInvestmentModels.has("license") ||
              selectedSitesInvestmentModels.has("none")) && (
              <Dropdown.Item
                as="button"
                onClick={() =>
                  fetchSitesReport.mutate({
                    siteIds: Array.from(selectedSiteIds),
                    prefixName: organisation ? `org-${organisation.name}` : "",
                    reportName: "customer_cashflow",
                    fileName: "cashflow",
                    options: {
                      cashflowType: "landlord-license",
                      periodType: "annual",
                      ...(isReportDebug && { debug: true }),
                    },
                  })
                }
              >
                Download Customer cashflow (Landlord, License)
              </Dropdown.Item>
            )}
          {(selectedSitesOwnershipTypes.has("landlord") ||
            selectedSitesOwnershipTypes.has("tenant")) &&
            (selectedSitesInvestmentModels.has("lease") ||
              selectedSitesInvestmentModels.has("none")) && (
              <Dropdown.Item
                as="button"
                onClick={() =>
                  fetchSitesReport.mutate({
                    siteIds: Array.from(selectedSiteIds),
                    prefixName: organisation ? `org-${organisation.name}` : "",
                    reportName: "customer_cashflow",
                    fileName: "cashflow",
                    options: {
                      cashflowType: "landlord-lease",
                      periodType: "annual",
                      ...(isReportDebug && { debug: true }),
                    },
                  })
                }
              >
                Download Customer cashflow (Landlord, Lease)
              </Dropdown.Item>
            )}
          {selectedSitesOwnershipTypes.has("ownerOccupier") &&
            (selectedSitesInvestmentModels.has("license") ||
              selectedSitesInvestmentModels.has("none")) && (
              <Dropdown.Item
                as="button"
                onClick={() =>
                  fetchSitesReport.mutate({
                    siteIds: Array.from(selectedSiteIds),
                    prefixName: organisation ? `org-${organisation.name}` : "",
                    reportName: "customer_cashflow",
                    fileName: "cashflow",
                    options: {
                      cashflowType: "ownerOccupier-license",
                      periodType: "annual",
                      ...(isReportDebug && { debug: true }),
                    },
                  })
                }
              >
                Download Customer cashflow (Owner-Occupier, License)
              </Dropdown.Item>
            )}
          {selectedSitesOwnershipTypes.has("ownerOccupier") &&
            (selectedSitesInvestmentModels.has("lease") ||
              selectedSitesInvestmentModels.has("none")) && (
              <Dropdown.Item
                as="button"
                onClick={() =>
                  fetchSitesReport.mutate({
                    siteIds: Array.from(selectedSiteIds),
                    prefixName: organisation ? `org-${organisation.name}` : "",
                    reportName: "customer_cashflow",
                    fileName: "cashflow",
                    options: {
                      cashflowType: "ownerOccupier-lease",
                      periodType: "annual",
                      ...(isReportDebug && { debug: true }),
                    },
                  })
                }
              >
                Download Customer cashflow (Owner-Occupier, Lease)
              </Dropdown.Item>
            )}
          <Dropdown.Item
            as="button"
            onClick={() =>
              fetchSitesReport.mutate({
                siteIds: Array.from(selectedSiteIds),
                prefixName: organisation ? `org-${organisation.name}` : "",
                reportName: "inrange_spv",
                fileName: "spv-cashflow",
                options: {
                  ...(isReportDebug && { debug: true }),
                },
              })
            }
          >
            Download SPV cashflow
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() =>
              fetchSitesReport.mutate({
                siteIds: Array.from(selectedSiteIds),
                prefixName: organisation ? `org-${organisation.name}` : "",
                reportName: "amazon_cashflow",
                fileName: "amazon-cashflow",
              })
            }
          >
            Download Amazon cashflow
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() =>
              fetchSitesReport.mutate({
                siteIds: Array.from(selectedSiteIds),
                prefixName: organisation ? `org-${organisation.name}` : "",
                reportName: "amazon_ppa_annex_a",
                fileName: "amazon-ppa-annex-a",
              })
            }
          >
            Download Amazon PPA Annex A report
          </Dropdown.Item>
          <Dropdown.Item
            as="button"
            onClick={() =>
              fetchSitesReport.mutate({
                siteIds: Array.from(selectedSiteIds),
                prefixName: organisation ? `org-${organisation.name}` : "",
                options: {
                  version: "v2",
                  ...(isReportDebug && { debug: true }),
                },
                reportName: "prologis_cashflow",
                fileName: "prologis-cashflow",
              })
            }
          >
            Download Prologis cashflow
          </Dropdown.Item>
          <Dropdown.Item
            as="div"
            onClick={(e) => {
              e.stopPropagation();
              setIsReportDebug(!isReportDebug);
            }}
          >
            <Form.Check
              type="switch"
              label="Enable debug data in reports"
              checked={isReportDebug}
              readOnly
            />
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      {bulkEditMode === "edit-siteinfo" && (
        <BulkEditSiteInformation
          organisation={organisation}
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
      {bulkEditMode === "edit-dealterms" && (
        <BulkEditDealTerms
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
      {bulkEditMode === "edit-generation" && (
        <BulkEditGeneration
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
      {bulkEditMode === "edit-costs" && (
        <BulkEditCosts
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
      {bulkEditMode === "edit-tariffs" && (
        <BulkEditExportNetworkTariff
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
      {bulkEditMode === "edit-supplier" && (
        <BulkEditSupplierTariff
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
      {bulkEditMode === "edit-financial-performance" && (
        <BulkEditFinancialPerformanceInRange
          organisation={organisation}
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
      {bulkEditMode === "automate-scoring" && (
        <BulkAutomateScoring
          sites={sites}
          selectedSiteIds={selectedSiteIds}
          onSitesUpdate={onSitesUpdate}
          onClose={() => setBulkEditMode(undefined)}
        />
      )}
    </>
  );
};

export default SitesTableBulkEditMenu;
